import { AxiosError } from 'axios';
import { ResourceKey } from 'i18next';

import { generalInformation } from '~constants';
import i18n from '~lib/i18n';

export type ErrorCode = keyof typeof ApiError.errorCodeMapping;

class ApiError {
	// The Axios error if required for custom error handling.
	public axiosError: AxiosError;
	// The error code associated with the error.
	public errorCode?: ErrorCode;
	// A human readable description of the error.
	public detail?: string;
	// The title of the error.
	private title?: string;
	// The URI to which the call that triggered the error was made.
	private instance?: string;
	// The request method that was used to call to the endpoint where the error occurred.
	private requestMethod?: string;
	// The error description that's to be shown to the user.
	private description: string;

	constructor(error: AxiosError) {
		const responseData = error.response?.data as {
			// Normal error model.
			title?: string;
			detail?: string;
			instance?: string;
			errorCode?: string;
			requestMethod?: string;

			// Validation error fields
			status: number;
			errors: object;
		};

		this.axiosError = error;
		this.title = responseData?.title;
		this.detail = responseData?.detail;
		this.instance = responseData?.instance;
		this.errorCode = responseData?.errorCode as ErrorCode;
		this.requestMethod = responseData?.requestMethod;
		let effectiveDescription: string = i18n.t('unknownError', { ns: 'errors' });

		// If we have a malformed request, the backend throws a different error structure.
		// To not expose too much sensitive/technical data on production, we only show the
		// errors on non prod environments
		if (responseData?.errors !== undefined) {
			const isWebhookEndpoint = this.axiosError.request?.responseURL?.includes('webhook');
			this.errorCode = 'validationError';
			effectiveDescription =
				generalInformation.isProduction && !isWebhookEndpoint ?
					'Validation error'
				:	'Validation error:' + responseData.status + ' => ' + JSON.stringify(responseData?.errors);
		} else {
			// Look for the code in our specific mapping
			if (this.errorCode && this.errorCode in ApiError.errorCodeMapping) {
				// If we have a backend-code use that.
				switch (this.errorCode) {
					case 'SKC_010':
						// SKC_010 is a bad request, often including a reason why the request is bad.
						// Since there's only a single error code for bad requests, we can't give a proper translated error.
						// Therefore we show an error with the backend details included.
						effectiveDescription = i18n.t('executedInvalidCommand', {
							ns: 'errors',
							details: this.detail ?? '',
						});
						break;
					default:
						effectiveDescription = i18n.t(ApiError.errorCodeMapping[this.errorCode], {
							ns: 'errors',
						});
				}
			} else {
				// Fall back on http codes
				switch (error.response?.status) {
					case 403:
						this.errorCode = 'SKC_003';
						effectiveDescription = i18n.t('server.forbiddenException', { ns: 'errors' });
						break;
					case 404:
						this.errorCode = 'SKC_004';
						effectiveDescription = i18n.t('server.notFoundException', { ns: 'errors' });
						break;
					case 500:
					case 501:
					case 502:
					case 503:
						effectiveDescription = i18n.t('server.badGatewayException', { ns: 'errors' });
						break;
					default:
						console.warn(
							`Unhandled error state. Status: ${error.response?.status}, Code: ${this.errorCode}`
						);
				}
			}
		}
		this.description = effectiveDescription;
	}

	/**
	 * Overrides the default error.
	 *
	 * @param value the new error description.
	 */
	public setDescription(value: string) {
		this.description = value;
	}

	/**
	 * Returns the error that should be shown to the user.
	 *
	 * @returns The error description.
	 */
	public getDescription(): string {
		return this.description;
	}

	/**
	 * Contains all error codes know from the backend. These have solid default translations that
	 * will automatically be set in the description field. If needed the code can be checked and
	 * setDescription(value:string) can be used to set a custom error from within a service.
	 */
	static errorCodeMapping = {
		// Server
		CLI_001: 'server.clientExistsException',
		CLI_002: 'server.clientNotFoundException',
		DEV_001: 'server.deviceAssignedOrNotFoundException',
		DEV_002: 'server.deviceNotFoundException',
		DGR_001: 'server.deviceGroupNotFoundException',
		DOK_001: 'server.offlineKeyNotFoundException',
		DOK_002: 'server.offlineKeyExistsException',
		DOK_003: 'server.offlineKeyInvalidKeyTypeException',
		DOK_004: 'server.offlineKeyInvalidDeviceTypeException',
		KEY_001: 'server.keyAssignedOrNotFoundException',
		KEY_002: 'server.keyNotFoundException',
		KGR_001: 'server.keyGroupNotFoundException',
		REX_001: 'server.recurrenceExceptionExistsException',
		REX_002: 'server.recurrenceExceptionNotFoundException',
		RSG_001: 'server.resourceGroupNotFoundException',
		RSG_002: 'server.resourceGroupExistsException',
		RSG_003: 'server.resourceGroupContainsEntitiesException',
		RSH_001: 'server.recurrenceScheduleNotFoundException',
		WHK_001: 'server.invalidWebhookResponseException',
		WHK_002: 'server.rentalWebHookExpectedUnauthorisedException',
		WHK_003: 'server.rentalWebHookExpectedFailureException',
		SKC_002: 'server.badGatewayException',
		SKC_003: 'server.forbiddenException',
		SKC_004: 'server.notFoundException',
		SKC_005: 'server.unprocessableEntityException',
		SKC_006: 'server.conflictException',
		SKC_007: 'server.invalidEmailException',
		SKC_008: 'server.invalidPhoneNumberException',
		SKC_009: 'server.invalidCultureException',
		SKC_010: 'server.badRequestException',
		SKC_011: 'server.typeMismatchException',
		TRG_001: 'server.triggerInvalidSearchParametersException',
		TRG_002: 'server.triggerNotFoundException',
		TRI_002: 'server.triggerSourceExistsException',
		USR_001: 'server.userNotFoundException',
		USR_002: 'server.userExistsException',
		USR_003: 'server.userInvalidRoleException',
		USR_004: 'server.userDeletionFailedException',
		CMD_001: 'server.commandBadRequestException',

		// Custom
		unexpectedEmptyResponse: 'unexpectedEmptyResponse',
		exportCompletedWithoutJobId: 'exportCompletedWithoutJobId',
		validationError: 'somethingWentWrong',
	} as const satisfies Record<string, ResourceKey>;

	static createFromCustomCode(code: ErrorCode): ApiError {
		return new ApiError({
			response: { data: { errorCode: code } },
		} as AxiosError);
	}
}

export default ApiError;
